import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motionTokens } from "./motion";

const GSAP_SPEED_MULTIPLIER = 1.3;
const DEFAULT_CINEMATIC_FROM = Object.freeze({
  y: 30,
  opacity: 0,
  scale: 0.985,
  filter: "blur(8px)",
});
const DEFAULT_CINEMATIC_TO = Object.freeze({
  y: 0,
  opacity: 1,
  scale: 1,
  filter: "blur(0px)",
  duration: motionTokens.duration850,
  ease: "power3.out",
});

// Register plugins once when this module is imported.
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
  if (typeof gsap.globalTimeline?.timeScale === "function") {
    gsap.globalTimeline.timeScale(GSAP_SPEED_MULTIPLIER);
  }
}

const playedAnimations = new Set();
let reducedMotionMediaQuery = null;

export function shouldReduceMotion() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  if (!reducedMotionMediaQuery) {
    reducedMotionMediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  }
  return Boolean(reducedMotionMediaQuery.matches);
}

function normalizeTargets(targets) {
  if (!targets) return [];
  if (typeof targets === "string") {
    if (typeof document === "undefined") return [];
    return Array.from(document.querySelectorAll(targets));
  }
  if (typeof Element !== "undefined" && targets instanceof Element) {
    return [targets];
  }
  if (
    Array.isArray(targets) ||
    (typeof NodeList !== "undefined" && targets instanceof NodeList)
  ) {
    return Array.from(targets).filter(Boolean);
  }
  return [];
}

function stripTweenOnlyVars(vars = {}) {
  const {
    duration,
    delay,
    ease,
    stagger,
    scrollTrigger,
    onComplete,
    onUpdate,
    repeat,
    yoyo,
    keyframes,
    snap,
    clearProps,
    ...rest
  } = vars;
  return rest;
}

/**
 * Ensures the provided GSAP animation only runs a single time per page load.
 * Returns any cleanup function the animation produces so the caller can still
 * revert GSAP contexts on unmount.
 */
export function runGsapOnce(key, animationFactory) {
  if (typeof window === "undefined") {
    return undefined;
  }
  if (shouldReduceMotion()) {
    return undefined;
  }

  if (!key) {
    throw new Error("runGsapOnce requires a unique key.");
  }

  if (playedAnimations.has(key)) {
    return undefined;
  }

  playedAnimations.add(key);

  try {
    const cleanup =
      typeof animationFactory === "function" ? animationFactory() : undefined;
    return typeof cleanup === "function" ? cleanup : undefined;
  } catch (error) {
    playedAnimations.delete(key);
    throw error;
  }
}

export function setToEndState(targets, vars = {}) {
  const elements = normalizeTargets(targets);
  if (!elements.length) return;
  gsap.set(elements, {
    ...stripTweenOnlyVars(vars),
    clearProps: vars.clearProps || "transform,filter,opacity",
  });
}

export function cinematicReveal({
  targets,
  trigger,
  start = "top 85%",
  once = true,
  from = DEFAULT_CINEMATIC_FROM,
  to = DEFAULT_CINEMATIC_TO,
  stagger = 0,
  delay = 0,
  useScrollTrigger = true,
} = {}) {
  const elements = normalizeTargets(targets);
  if (!elements.length) return undefined;

  const tweenTo = {
    ...to,
    stagger,
    delay,
    clearProps: "transform,filter,opacity",
  };

  if (shouldReduceMotion()) {
    setToEndState(elements, tweenTo);
    return undefined;
  }

  if (useScrollTrigger) {
    tweenTo.scrollTrigger = {
      trigger: trigger || elements[0],
      start,
      once,
    };
  }

  return gsap.fromTo(elements, { ...from }, tweenTo);
}

export function animateFeedback({
  target,
  from,
  to,
  clearProps = "transform,filter,opacity",
} = {}) {
  if (!target) return undefined;
  if (shouldReduceMotion()) {
    setToEndState(target, {
      ...(to || {}),
      clearProps,
    });
    return undefined;
  }
  return gsap.fromTo(target, from || {}, {
    ...(to || {}),
    clearProps,
  });
}

export function revealHeadingWords({
  container,
  wordSelector,
  trigger,
  start = "top 85%",
  fromY = 42,
  duration = motionTokens.duration700,
  ease = "power2.inOut",
  stagger = motionTokens.duration100,
} = {}) {
  if (!container || !wordSelector) return;
  const words = container.querySelectorAll(wordSelector);
  if (!words.length) return;
  if (shouldReduceMotion()) {
    setToEndState(words, { y: 0, opacity: 1, clearProps: "transform,opacity" });
    return;
  }
  const resolvedTrigger = trigger || container;
  gsap.set(words, { y: fromY, opacity: 0 });
  gsap.to(words, {
    scrollTrigger: {
      trigger: resolvedTrigger,
      start,
      once: true,
    },
    y: 0,
    opacity: 1,
    duration,
    ease,
    stagger,
  });
}

export function revealElement({
  element,
  trigger,
  start = "top 85%",
  from = { y: 24, opacity: 0 },
  to = {
    y: 0,
    opacity: 1,
    duration: motionTokens.duration750,
    ease: "power2.out",
    delay: motionTokens.duration100,
  },
} = {}) {
  if (!element) return;
  if (shouldReduceMotion()) {
    setToEndState(element, { ...(to || {}), y: 0, opacity: 1 });
    return;
  }
  const resolvedTrigger = trigger || element;
  gsap.set(element, { ...from });
  gsap.to(element, {
    scrollTrigger: {
      trigger: resolvedTrigger,
      start,
      once: true,
    },
    ...to,
  });
}

export { gsap, ScrollTrigger };
